import { useLocalStorageState } from "@outschool/local-storage";
import React from "react";

import { PageAlertProps } from "./PageAlertTypes";

export type PageAlertArgs = PageAlertProps;

type PageAlerts = {
  [id: PageAlertProps["id"]]: PageAlertProps;
};

type PageAlertsContext = {
  pageAlerts: PageAlerts;
  addPageAlert(alert: PageAlertArgs): void;
  removePageAlert(id: string): void;
  muteAlert(id: string): void;
  isMuted: (id: string) => boolean;
};

const PageAlertsContext = React.createContext<PageAlertsContext>({
  pageAlerts: {},
  addPageAlert: () => null,
  removePageAlert: () => null,
  muteAlert: () => null,
  isMuted: () => false,
});

const MUTED_ALERTS_LOCAL_STORAGE_KEY = "os-muted-page-alerts";

const getUniqueId = (id: string) => `os-page-alert-id-${id}`;

const PageAlertsProvider = (props: React.PropsWithChildren<{}>) => {
  const [pageAlerts, setPageAlerts] = React.useState<PageAlerts>({});
  // Track all muted alerts so they stay muted between page loads
  const [mutedAlerts, setMutedAlerts] = useLocalStorageState<string[]>(
    MUTED_ALERTS_LOCAL_STORAGE_KEY,
    [] as string[]
  );

  const addPageAlert = React.useCallback(
    (alert: PageAlertArgs) =>
      setPageAlerts(prevAlerts => {
        const { id } = alert;
        if (mutedAlerts.includes(id)) {
          return prevAlerts;
        }
        const uniqueId = getUniqueId(id);
        return {
          ...prevAlerts,
          [uniqueId]: alert,
        };
      }),
    [mutedAlerts, setPageAlerts]
  );

  const removePageAlert = React.useCallback(
    (id: string) =>
      setPageAlerts(prevAlerts => {
        const uniqueId = getUniqueId(id);
        const { [uniqueId]: _alertToRemove, ...rest } = prevAlerts;
        return rest;
      }),
    [setPageAlerts]
  );

  const muteAlert = React.useCallback(
    (id: string) => {
      setMutedAlerts((prevMutedAlerts: string[]) => [...prevMutedAlerts, id]);
    },
    [setMutedAlerts]
  );

  const isMuted = React.useCallback(
    (id: string) => mutedAlerts.includes(id),
    [mutedAlerts]
  );

  return (
    <PageAlertsContext.Provider
      value={{
        pageAlerts,
        addPageAlert,
        removePageAlert,
        muteAlert,
        isMuted,
      }}
      {...props}
    />
  );
};

export default React.memo(PageAlertsProvider);

export const usePageAlerts = () => React.useContext(PageAlertsContext);
